import type {
  PreviewedCluster,
  PreviewedKeyword,
} from '@/services/googleAdsCampaignsService';

export type PlannerTrendPoint = {
  key: string;
  label: string;
  searches: number;
};

export const GOOGLE_ADS_EXPORT_BASE_HEADERS = [
  'Ad group',
  'Keyword',
  'Currency',
  'Avg. monthly searches',
  'Three month change',
  'YoY change',
  'Competition',
  'Competition (indexed value)',
  'Top of page bid (low range)',
  'Top of page bid (high range)',
  'Ad impression share',
  'Organic impression share',
  'Organic average position',
  'In account?',
  'In plan?',
  'Low hanging fruit?',
  'Source',
  'Landing page',
];

export const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MONTH_INDEX: Record<string, number> = {
  JANUARY: 1,
  FEBRUARY: 2,
  MARCH: 3,
  APRIL: 4,
  MAY: 5,
  JUNE: 6,
  JULY: 7,
  AUGUST: 8,
  SEPTEMBER: 9,
  OCTOBER: 10,
  NOVEMBER: 11,
  DECEMBER: 12,
};

export function buildClustersCsv(clusters: PreviewedCluster[]): string {
  const trendHeaders = buildPlannerTrendHeaders(clusters);
  const rows = clusters.flatMap((cluster) =>
    cluster.keywords.map((kw) => {
      const trend = keywordTrendMap(kw);
      const deltas = keywordTrendDeltas(kw);
      return [
        cluster.cluster_name,
        kw.keyword,
        'USD',
        kw.kp_avg_monthly_searches ?? kw.volume,
        deltas.threeMonthChange,
        deltas.yoyChange,
        kw.kp_competition ?? '',
        kw.kp_competition_index ?? '',
        kw.kp_low_top_of_page_bid_micros != null
          ? (kw.kp_low_top_of_page_bid_micros / 1_000_000).toFixed(2)
          : '',
        kw.kp_high_top_of_page_bid_micros != null
          ? (kw.kp_high_top_of_page_bid_micros / 1_000_000).toFixed(2)
          : '',
        formatShare(kw.ad_impression_share),
        formatShare(kw.organic_impression_share),
        kw.organic_average_position != null
          ? kw.organic_average_position.toFixed(1)
          : '',
        formatNullableBoolean(kw.in_account),
        formatNullableBoolean(kw.in_plan),
        lowHangingFruitLabel(kw, clusters),
        kw.source ?? 'brainstorm',
        cluster.landing_page ?? '',
        ...trendHeaders.map((header) => trend.get(header.key) ?? ''),
      ];
    }),
  );
  const headers = [
    ...GOOGLE_ADS_EXPORT_BASE_HEADERS,
    ...trendHeaders.map((header) => header.label),
  ];
  return [headers, ...rows]
    .map((row) => row.map(csvEscape).join(','))
    .join('\n');
}

const BUYER_INTENT_RE =
  /\b(agency|agencies|company|companies|service|services|provider|providers|firm|firms|consultant|consulting|hire|quote|demo|near me)\b/i;

function lowHangingFruitLabel(
  keyword: PreviewedKeyword,
  clusters: PreviewedCluster[],
): string {
  const allKeywords = clusters.flatMap((cluster) => cluster.keywords);
  const medianCpc = median(allKeywords.map((kw) => kw.cpc));
  const medianVolume = median(allKeywords.map((kw) => kw.volume));
  const volumeFloor = Math.max(100, medianVolume * 0.5);
  const cpcCeiling =
    medianCpc > 0 ? medianCpc * 1.25 : Number.POSITIVE_INFINITY;
  const competition = keyword.kp_competition?.toLowerCase();
  const competitionPass =
    competition === 'low' ||
    competition === 'medium' ||
    (keyword.kp_competition_index != null && keyword.kp_competition_index <= 50);

  if (
    BUYER_INTENT_RE.test(keyword.keyword) &&
    keyword.volume >= volumeFloor &&
    keyword.cpc > 0 &&
    keyword.cpc <= cpcCeiling &&
    competitionPass &&
    keyword.in_account !== true
  ) {
    return 'Yes';
  }
  return 'No';
}

function median(values: number[]): number {
  const sorted = values
    .filter((value) => Number.isFinite(value) && value > 0)
    .sort((a, b) => a - b);
  if (sorted.length === 0) return 0;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

export function buildKeywordPlannerTrendData(
  clusters: PreviewedCluster[],
): PlannerTrendPoint[] {
  const byMonth = new Map<string, { year: number; month: number; searches: number }>();
  const seenKeywordMonths = new Set<string>();
  clusters.forEach((cluster) => {
    cluster.keywords.forEach((keyword) => {
      const rows = keyword.kp_monthly_search_volumes ?? [];
      rows.forEach((row) => {
        const year = Number(row.year ?? 0);
        const month = normalizePlannerMonth(row.month);
        const searches = Number(row.monthly_searches ?? 0);
        if (!year || !month || Number.isNaN(searches)) return;
        const key = `${year}-${String(month).padStart(2, '0')}`;
        const dedupeKey = `${keyword.keyword.toLowerCase()}::${key}`;
        if (seenKeywordMonths.has(dedupeKey)) return;
        seenKeywordMonths.add(dedupeKey);
        const existing = byMonth.get(key);
        byMonth.set(key, {
          year,
          month,
          searches: (existing?.searches ?? 0) + searches,
        });
      });
    });
  });
  return Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-12)
    .map(([key, point]) => ({
      key,
      label: `${MONTH_LABELS[point.month - 1]} '${String(point.year).slice(-2)}`,
      searches: point.searches,
    }));
}

function buildPlannerTrendHeaders(clusters: PreviewedCluster[]) {
  const keys = new Map<string, { key: string; label: string }>();
  clusters.forEach((cluster) => {
    cluster.keywords.forEach((keyword) => {
      keywordTrendMap(keyword).forEach((_value, key) => {
        if (!keys.has(key)) {
          const [year, month] = key.split('-').map(Number);
          keys.set(key, {
            key,
            label: `Searches: ${MONTH_LABELS[month - 1]} ${year}`,
          });
        }
      });
    });
  });
  return Array.from(keys.values()).sort((a, b) => a.key.localeCompare(b.key));
}

function keywordTrendMap(keyword: PreviewedKeyword): Map<string, number> {
  const map = new Map<string, number>();
  (keyword.kp_monthly_search_volumes ?? []).forEach((row) => {
    const year = Number(row.year ?? 0);
    const month = normalizePlannerMonth(row.month);
    const searches = Number(row.monthly_searches ?? 0);
    if (!year || !month || Number.isNaN(searches)) return;
    map.set(`${year}-${String(month).padStart(2, '0')}`, searches);
  });
  return map;
}

function keywordTrendDeltas(keyword: PreviewedKeyword) {
  const points = Array.from(keywordTrendMap(keyword).entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, searches]) => ({ key, searches }));
  const latest = points.at(-1);
  const priorQuarter = points.at(-4);
  const priorYear = latest
    ? points.find((point) => point.key === previousYearKey(latest.key))
    : undefined;
  return {
    threeMonthChange: formatTrendChange(latest?.searches, priorQuarter?.searches),
    yoyChange: formatTrendChange(latest?.searches, priorYear?.searches),
  };
}

function normalizePlannerMonth(month: string | null | undefined): number | null {
  if (!month) return null;
  const raw = String(month).trim().toUpperCase();
  if (!raw || raw === 'UNSPECIFIED' || raw === 'UNKNOWN') return null;
  const numeric = Number(raw);
  if (Number.isInteger(numeric) && numeric >= 1 && numeric <= 12) {
    return numeric;
  }
  return MONTH_INDEX[raw] ?? null;
}

function previousYearKey(key: string): string {
  const [year, month] = key.split('-').map(Number);
  return `${year - 1}-${String(month).padStart(2, '0')}`;
}

function formatTrendChange(
  current: number | undefined,
  previous: number | undefined,
): string {
  if (current == null || previous == null || previous === 0) {
    return '';
  }
  const pct = ((current - previous) / previous) * 100;
  return `${pct > 0 ? '+' : ''}${pct.toFixed(0)}%`;
}

function formatShare(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '';
  if (value >= 0 && value <= 1) {
    return `${(value * 100).toFixed(2)}%`;
  }
  return `${value.toFixed(2)}%`;
}

function formatNullableBoolean(value: boolean | null | undefined): string {
  if (value == null) return '';
  return value ? 'Yes' : 'No';
}

function csvEscape(value: unknown): string {
  const text = value == null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}
